import { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "@clerk/clerk-react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { useSchool } from "@/context/SchoolContext";
import { BACKEND_URL } from "@/constants/api";
import { useAbortableLoader, isAbortError } from "@/hooks/useAbortableLoader";
import { BookOpen, ChevronLeft, AlertCircle, User, UserCog } from "lucide-react";
import { PageContainer } from "@/layouts/PageContainer";
import { PageHeader } from "@/layouts/PageHeader";
import { ReportFolderCard } from "@/components/entities/ReportFolderCard";
import { Button } from "@/components/ui/button";
import { Modal, ModalBody, ModalFooter } from "@/components/modals/Modal";
import { PaywallPrompt } from "@/components/ui/PaywallPrompt";
import { EmptyState } from "@/components/ui/EmptyState";

function truncateOen(raw) {
  const trimmed = raw?.trim() ?? "";
  if (!trimmed) return null;
  return trimmed.length > 9 ? trimmed.slice(0, 9) + "…" : trimmed;
}

function ReadOnlyReportRow({ report }) {
  const oenDisplay = truncateOen(report.data?.oen);
  return (
    <div className="rounded-xl overflow-hidden bg-white border dark:bg-primary dark:border-transparent flex items-center">
      <div className="flex-1 flex items-center px-4 py-3 gap-4 min-w-0">
        <div className="w-[100px] shrink-0">
          {oenDisplay ? (
            <span className="block text-center text-sm bg-brand-50 border border-brand-200 text-brand-700 dark:bg-white/10 dark:border-white/20 dark:text-white/100 rounded-md px-2 py-0.5 font-mono">
              {oenDisplay}
            </span>
          ) : (
            <span className="block text-center text-sm bg-brand-50/50 border border-brand-100 text-neutral-400 italic dark:bg-white/10 dark:border-white/20 dark:text-white/30 rounded-md px-2 py-0.5">
              No OEN
            </span>
          )}
        </div>
        <p className="flex-1 min-w-0 text-sm font-medium text-neutral-900 dark:text-white truncate">
          {report.studentName?.trim() || "Unnamed student"}
        </p>
        <span className="text-xs text-muted-foreground shrink-0">{report.reportType}</span>
      </div>
    </div>
  );
}

function AssignOwnerModal({ open, onClose, schoolId, reportFolderId, reportFolderName, currentOwnerId, onSuccess }) {
  const { getToken } = useAuth();
  const [members, setMembers] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelectedId("");
    const ac = new AbortController();
    (async () => {
      setLoading(true);
      try {
        const token = await getToken();
        const res = await fetch(`${BACKEND_URL}api/schools/${schoolId}/members`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: ac.signal,
        });
        if (!res.ok) throw new Error("Failed to load members");
        const data = await res.json();
        setMembers(data.filter((m) => m._id !== currentOwnerId));
      } catch (err) {
        if (isAbortError(err)) return;
        toast.error("Could not load school members");
        onClose();
      } finally {
        if (!ac.signal.aborted) setLoading(false);
      }
    })();
    return () => ac.abort();
  }, [open, schoolId, currentOwnerId, getToken, onClose]);

  async function handleSubmit() {
    if (!selectedId) return;
    setSubmitting(true);
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}api/schools/${schoolId}/report-folders/${reportFolderId}/owner`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ newOwnerId: selectedId }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Transfer failed");
      }
      toast.success("Report Folder ownership transferred");
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Reassign Owner"
      subtitle={reportFolderName ? `Transfer "${reportFolderName}" to another teacher.` : "Transfer this Report Folder to another teacher."}
      icon={<UserCog size={16} className="text-primary" />}
      maxWidth="max-w-sm"
    >
      <ModalBody>
        {loading ? (
          <div className="space-y-2">
            {[0, 1, 2].map((i) => <div key={i} className="h-10 bg-muted animate-pulse rounded-lg" />)}
          </div>
        ) : members.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            No other teachers in this school.
          </p>
        ) : (
          <div className="space-y-1.5">
            {members.map((m) => (
              <button
                key={m._id}
                onClick={() => setSelectedId(m._id)}
                className={`w-full text-left rounded-lg border px-4 py-2.5 transition-colors ${
                  selectedId === m._id
                    ? "border-primary bg-primary/5 text-foreground"
                    : "border-border bg-card text-foreground hover:bg-muted/50"
                }`}
              >
                <p className="text-sm font-medium leading-tight">{m.name || "Unnamed"}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{m.email}</p>
              </button>
            ))}
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button onClick={handleSubmit} disabled={!selectedId || submitting}>
          {submitting ? "Transferring…" : "Transfer Ownership"}
        </Button>
        <Button variant="outline" onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
}

function GroupingToggle({ value, onChange }) {
  const options = [
    { id: "year", label: "By year" },
    { id: "owner", label: "By teacher" },
  ];
  return (
    <div className="inline-flex items-center rounded-lg border border-border bg-muted/30 p-0.5">
      {options.map((o) => (
        <button
          key={o.id}
          onClick={() => onChange(o.id)}
          className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
            value === o.id
              ? "bg-card text-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function yearSortKey(year) {
  if (!year) return -1;
  const match = year.match(/\d{4}/);
  return match ? parseInt(match[0]) : -1;
}

function groupFolders(folders, mode) {
  if (mode === "owner") {
    const map = new Map();
    for (const f of folders) {
      const key = f.ownerId?._id ?? "__no_owner__";
      const label = f.ownerId?.name || f.ownerId?.email || "Unknown teacher";
      if (!map.has(key)) map.set(key, { label, items: [] });
      map.get(key).items.push(f);
    }
    return [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
  }
  const sorted = [...folders].sort(
    (a, b) => yearSortKey(b.schoolYear) - yearSortKey(a.schoolYear)
  );
  const map = new Map();
  for (const f of sorted) {
    const key = f.schoolYear ?? "__no_year__";
    const label = f.schoolYear || "No Year";
    if (!map.has(key)) map.set(key, { label, items: [] });
    map.get(key).items.push(f);
  }
  return [...map.values()];
}

function SchoolReportFolderList({ schoolId }) {
  const { getToken } = useAuth();
  const [reportFolders, setReportFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paywalled, setPaywalled] = useState(false);
  const [groupBy, setGroupBy] = useState("year");
  const [reassigning, setReassigning] = useState(null);

  const reloadKey = useCallback(async (ac) => {
    try {
      setLoading(true);
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}api/schools/${schoolId}/report-folders`, {
        headers: { Authorization: `Bearer ${token}` },
        signal: ac.signal,
      });
      if (res.status === 402) { setPaywalled(true); return; }
      if (!res.ok) throw new Error("Failed to load Report Folders");
      setReportFolders(await res.json());
    } catch (err) {
      if (isAbortError(err)) return;
      setError(err.message);
    } finally {
      if (!ac.signal.aborted) setLoading(false);
    }
  }, [schoolId, getToken]);

  useEffect(() => {
    const ac = new AbortController();
    reloadKey(ac);
    return () => ac.abort();
  }, [reloadKey]);

  const groups = useMemo(() => groupFolders(reportFolders, groupBy), [reportFolders, groupBy]);

  if (loading) return (
    <div className="space-y-3">
      {[0, 1, 2].map((i) => <div key={i} className="h-14 bg-muted animate-pulse rounded-xl" />)}
    </div>
  );

  if (paywalled) return <PaywallPrompt />;

  if (error) return (
    <div className="flex items-center gap-2 text-destructive text-sm rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3">
      <AlertCircle size={15} />{error}
    </div>
  );

  if (reportFolders.length === 0) return (
    <EmptyState
      icon={BookOpen}
      title="No Report Folders yet"
      description="When teachers in your school create Report Folders, they will appear here."
    />
  );

  return (
    <>
      <AssignOwnerModal
        open={!!reassigning}
        onClose={() => setReassigning(null)}
        schoolId={schoolId}
        reportFolderId={reassigning?._id}
        reportFolderName={reassigning?.name}
        currentOwnerId={reassigning?.ownerId?._id}
        onSuccess={() => {
          const ac = new AbortController();
          reloadKey(ac);
        }}
      />

      <div className="flex items-center justify-between gap-3 mb-5">
        <p className="text-sm text-muted-foreground">
          {reportFolders.length} Report Folder{reportFolders.length !== 1 ? "s" : ""} across your school
        </p>
        <GroupingToggle value={groupBy} onChange={setGroupBy} />
      </div>

      <div className="space-y-5">
        {groups.map((g) => (
          <div key={g.label} className="space-y-2">
            <div className="flex items-center gap-3">
              <div className="h-0.5 flex-1 bg-border" />
              <span className="text-lg font-bold text-foreground">{g.label}</span>
              <div className="h-0.5 flex-1 bg-border" />
            </div>
            <div className="flex flex-wrap gap-2">
              {g.items.map((folder) => (
                <ReportFolderCard
                  key={folder._id}
                  item={folder}
                  getLinkPath={(f) => `/schools/report-folders/${f._id}`}
                  ownerName={folder.ownerId?.name}
                  getMenuItems={() => [
                    {
                      label: "Reassign owner",
                      icon: UserCog,
                      onClick: (e) => {
                        e?.preventDefault?.();
                        setReassigning(folder);
                      },
                    },
                  ]}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

function ReportFolderDetail({ schoolId, reportFolderId }) {
  const { getToken } = useAuth();
  const [school, setSchool] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paywalled, setPaywalled] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);

  const load = useCallback(async (signal) => {
    try {
      setLoading(true);
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };
      const [schoolRes, detailRes] = await Promise.all([
        fetch(`${BACKEND_URL}api/schools/${schoolId}`, { headers, signal }),
        fetch(`${BACKEND_URL}api/schools/${schoolId}/report-folders/${reportFolderId}`, { headers, signal }),
      ]);
      if (detailRes.status === 402) { setPaywalled(true); return; }
      if (!detailRes.ok) {
        const body = await detailRes.json().catch(() => ({}));
        throw new Error(body.message || "Failed to load Report Folder");
      }
      if (schoolRes.ok) setSchool(await schoolRes.json());
      setDetail(await detailRes.json());
    } catch (err) {
      if (isAbortError(err)) return;
      setError(err.message);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [schoolId, reportFolderId, getToken]);

  useAbortableLoader(load);

  if (loading) return (
    <PageContainer>
      <div className="h-8 w-40 bg-muted animate-pulse rounded mb-6" />
      <div className="h-48 bg-muted animate-pulse rounded-2xl" />
    </PageContainer>
  );

  if (paywalled) return (
    <PageContainer>
      <Link to="/schools/report-folders" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6">
        <ChevronLeft size={14} />
        Folder Ownership
      </Link>
      <PaywallPrompt />
    </PageContainer>
  );

  if (error) return (
    <PageContainer>
      <div className="flex items-center gap-2 text-destructive text-sm rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3">
        <AlertCircle size={15} />{error}
      </div>
    </PageContainer>
  );

  const label =
    detail?.name ||
    [detail?.grade && `Grade ${detail.grade}`, detail?.schoolYear].filter(Boolean).join(" · ");

  return (
    <PageContainer>
      <AssignOwnerModal
        open={showAssignModal}
        onClose={() => setShowAssignModal(false)}
        schoolId={schoolId}
        reportFolderId={reportFolderId}
        reportFolderName={label}
        currentOwnerId={detail?.ownerId?._id}
        onSuccess={load}
      />

      <Link
        to="/schools/report-folders"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6"
      >
        <ChevronLeft size={14} />
        Folder Ownership
      </Link>

      <div className="flex items-start justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
            <BookOpen size={18} className="text-primary" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-normal text-foreground">{label}</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Owned by{" "}
              <span className="font-medium text-foreground">
                {detail?.ownerId?.name || "Unknown"}
              </span>
              {[detail?.grade && `Grade ${detail.grade}`, detail?.schoolYear]
                .filter(Boolean)
                .map((s) => ` · ${s}`)
                .join("")}
            </p>
          </div>
        </div>
        <Button
          size="sm"
          className="shrink-0 gap-1.5"
          onClick={() => setShowAssignModal(true)}
        >
          <UserCog size={14} />
          Reassign Owner
        </Button>
      </div>

      <div className="bg-card border border-border rounded-2xl overflow-hidden">
        <div className="px-5 py-3 border-b border-border">
          <p className="text-sm font-medium text-foreground">
            Students ({detail?.reports?.length ?? 0})
          </p>
        </div>
        {detail?.reports?.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-12 text-center">
            <User size={28} className="text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">No student reports yet</p>
          </div>
        ) : (
          <div className="p-4 space-y-2">
            {detail?.reports?.map((report) => (
              <ReadOnlyReportRow key={report._id} report={report} />
            ))}
          </div>
        )}
      </div>
    </PageContainer>
  );
}

export default function ManageReportFolderOwnership() {
  const { memberships } = useSchool();
  const schoolId = memberships[0]?.school?._id;
  const { reportFolderId } = useParams();

  if (reportFolderId) {
    return <ReportFolderDetail schoolId={schoolId} reportFolderId={reportFolderId} />;
  }

  return (
    <PageContainer>
      <Link
        to="/schools"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6"
      >
        <ChevronLeft size={14} />
        My School
      </Link>
      <PageHeader
        title="Folder Ownership"
        subtitle="View every Report Folder in your school. Reassign folders when teachers leave or change classes."
      />
      <SchoolReportFolderList schoolId={schoolId} />
    </PageContainer>
  );
}
